import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {AuthService} from '../../core/services/auth-service.service';
import {ListContentModel} from '../../shared/models/list-content.model';
import {
  ContactAssignmentResource,
} from '../../shared/components/dropdowns/isp-contacts-dropdown/models/contact-assignment.resource';
import {ContactTypeEnum} from '../../shared/components/dropdowns/isp-contacts-dropdown/models/contacts.enum';
import {ContactModel, ContactPostResource, ContactsAdvancedSearchModel} from '../../company/models/contacts.model';
import {EmployeeAdvancedSearchModel} from '../models/employee-advanced-search.model';
import {Authority, EmployeeWithEntityModel} from '../models/employee-post-body.model';
import {ProjectManagerModel} from '../models/project-manager.model';
import {commonProperties} from '../../../assets/environments/environment.common';
import {environment} from '../../../assets/environments/environment';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {

  constructor(private httpClient: HttpClient, private authService: AuthService) {}

  contactAssignmentProcess(contacts: any[]): ContactAssignmentResource[] {
    return contacts?.map(({id, type, employeeId, contactId}) => ({
      contactId: !type || type === ContactTypeEnum.CONTACT ? id ?? contactId : null,
      employeeId: type === ContactTypeEnum.EMPLOYEE ? id ?? employeeId : null,
      type: type ?? 'CONTACT',
    })).filter(item => item.contactId || item.employeeId);
  }

  mapAdvancedSearchForm(params: any): EmployeeAdvancedSearchModel {
    return new EmployeeAdvancedSearchModel(params);
  }

  getEmployeesByPage(page: number, size: number, sort: string, search: string, advancedSearch: EmployeeAdvancedSearchModel, isAdvancedSearchDisplayed: boolean): Observable<ListContentModel> {
    return this.fetchEmployeesByPage(page, size, sort, search, advancedSearch, isAdvancedSearchDisplayed, null);
  }

  getCompanyEmployeesByPage(
    companyId: string,
    page: number,
    size: number,
    sort: string,
    search: string,
    advancedSearch: EmployeeAdvancedSearchModel,
    allowDisabled: boolean,
  ): Observable<ListContentModel> {
    return this.fetchEmployeesByPage(page, size, sort, search, advancedSearch, false, allowDisabled);
  }

  private fetchEmployeesByPage(
    page: number,
    size: number,
    sort: string,
    search: string,
    advancedSearch: EmployeeAdvancedSearchModel,
    isAdvancedSearchDisplayed: boolean,
    allowDisabled: boolean | null,
  ): Observable<ListContentModel> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    } else if (advancedSearch) {
      if (advancedSearch.firstName) {
        params = params.set('firstName', advancedSearch.firstName);
      }
      if (advancedSearch.lastName) {
        params = params.set('lastName', advancedSearch.lastName);
      }
      if (advancedSearch.email) {
        params = params.set('email', advancedSearch.email);
      }
      if (isAdvancedSearchDisplayed && advancedSearch.disabled) {
        params = params.set('enabled', 'false');
      }
    }

    if (allowDisabled != null) {
      params = params.set('allowDisabled', allowDisabled);
    }

    params = params.set('size', size.toString()).set('page', page.toString()).set('sort', sort);
    return this.httpClient.get<ListContentModel>(environment.api_root + commonProperties.employeesList, {params})
      .pipe(
        map(data => {
          data.content.forEach(e => e.fullName = e.firstName + ' ' + e.lastName);
          return data;
        })
      );
  }

  mapFormValueToResource(value: any): EmployeeWithEntityModel {
    const params: EmployeeWithEntityModel = new EmployeeWithEntityModel();

    if (value.employeeId) {
      params.employeeId = value.employeeId;
    } else {
      params.firstName = value.firstName;
      params.lastName = value.lastName;
      params.email = value.email;
      params.phoneNumber = value?.phoneNumber?.internationalNumber?.replace(/\s/g, '').replace('-', '');
      params.title = value.title;
    }

    params.authorities = [];
    params.authorities.push(new Authority(value.authority.id, value.authority.name, this.authService.decodeToken('tenantId')));

    return params;
  }

  createEmployee(result) {
    return this.httpClient.post(environment.api_root + commonProperties.employeesList, result);
  }

  updateEmployee(params: EmployeeWithEntityModel): Observable<any> {
    return this.httpClient.patch(environment.api_root + commonProperties.employeeById.replace(':employeeId', params.employeeId), params);
  }

  getEmployeeById(employeeId: any) {
    return this.httpClient.get(environment.api_root + commonProperties.employeeById.replace(':employeeId', employeeId));
  }

  getMyProfile() {
    return this.httpClient.get(environment.api_root + commonProperties.myProfile.replace(':employeeId', this.authService.decodeToken('employeeId')));
  }

  getEmployeePictureByEmployeeId(employeeId: string) {
    return this.httpClient.get(environment.api_root + commonProperties.employeePictureById.replace(':employeeId', employeeId), {responseType: 'blob'});
  }

  deleteMyPicture() {
    return this.httpClient.delete<void>(environment.api_root + commonProperties.employeePictureById.replace(':employeeId', this.authService.decodeToken('employeeId')));
  }

  getMyPicture(): Observable<Blob> {
    const employeeId = this.authService.decodeToken('employeeId');
    const url = environment.api_root + commonProperties.employeePictureById.replace(':employeeId', employeeId);
    const headers = new HttpHeaders({
      'Cache-Control':  'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      'Pragma': 'no-cache',
      'Expires': '0'
    });
    const options = {
      headers: headers,
      responseType: 'blob' as 'json'
    };
    return this.httpClient.get<Blob>(url, options);
  }

  uploadMyAvatar(formData: any) {
    return this.httpClient.post(environment.api_root + commonProperties.myPicture.replace(':employeeId', this.authService.decodeToken('employeeId')), formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  updateEmployeeRoleByEmployeeId(employeeId: any, body) {
    return this.httpClient.put(environment.api_root + commonProperties.employeeRoleById.replace(':employeeId', employeeId), body);
  }

  updateMyProfile(body: any) {
    return this.httpClient.patch(environment.api_root + commonProperties.myProfile.replace(':employeeId', this.authService.decodeToken('employeeId')), body);
  }

  getEmployeeAuthorities() {
    return this.httpClient.get(environment.api_root + commonProperties.authorities);
  }

  getAllProjectManagers(search: string, page: number, size: number): Observable<ProjectManagerModel[]> {
    let params = new HttpParams();
    params = params.set('search', search).set('page', page.toString()).set('size', size.toString()).set('authority', 'PROJECT_MANAGER');
    return this.httpClient.get<ProjectManagerModel[]>(environment.api_root + commonProperties.projectManagers, {params});
  }

  testUserEmail(userEmail: string): Observable<any> {
    let params = new HttpParams();
    params = params.set('email', userEmail);
    return this.httpClient.get(environment.api_root + commonProperties.userEmailTest, {params});
  }

  getContactByPage(
    companyId: string,
    page: number,
    size: number,
    sort: string,
    search: string,
    advancedSearch: ContactsAdvancedSearchModel,
    allowDisabled?: boolean,
  ): Observable<ListContentModel> {
    let params = new HttpParams();
    params = params.set('size', size.toString()).set('page', page.toString()).set('sort', sort).set('companyId', companyId);

    if (search) {
      params = params.set('search', encodeURIComponent(search));
    }
    if (advancedSearch) {
      if (advancedSearch.firstName) {
        params = params.set('firstName', advancedSearch.firstName);
      }
      if (advancedSearch.lastName) {
        params = params.set('lastName', advancedSearch.lastName);
      }
      if (advancedSearch.email) {
        params = params.set('email', advancedSearch.email);
      }
      if (advancedSearch.phoneNumber) {
        params = params.set('phoneNumber', encodeURIComponent(advancedSearch.phoneNumber));
      }
      if (advancedSearch.jobTitle) {
        params = params.set('jobTitle', advancedSearch.jobTitle);
      }
      if (advancedSearch.attributions?.length > 0) {
        params = params.set('attributions', advancedSearch.attributions.join(','));
      }
    }
    if (allowDisabled != null) {
      params = params.set('allowDisabled', allowDisabled);
    }
    return this.httpClient.get<ListContentModel>(environment.api_root + commonProperties.contacts, {params});
  }

  createContact(params: ContactPostResource): Observable<any> {
    return this.httpClient.post(environment.api_root + commonProperties.contacts, params);
  }

  updateContact(contact: ContactPostResource, id: string, companyId: string): Observable<any> {
    const params = new HttpParams().set('companyId', companyId);
    return this.httpClient.put(`${environment.api_root}${commonProperties.contactById.replace(':contactId', id)}`, contact, {params});
  }

  getContactFromMail(companyId: string, email: string): Observable<ContactModel> {
    const params = new HttpParams().set('companyId', companyId).set('email', email);
    return this.httpClient.get<ContactModel>(environment.api_root + commonProperties.contactByEmail, {params});
  }

  getContactById(id: string, companyId: string): Observable<ContactModel> {
    const params = new HttpParams().set('companyId', companyId);
    return this.httpClient.get<ContactModel>(environment.api_root + commonProperties.contactByIdAndCompany.replace(':contactId', id), {params});
  }

  getUserByEmail(email: string): Observable<any> {
    return this.httpClient.get(`${environment.api_root}authMgtApi/users/email/${email}`);
  }
}
